// Motion tokens — Tier 1 feel pass (Sept 2026).
//
// Durations in ms, for both RN `Animated` (native) and CSS (web). Easings are
// CSS cubic-bezier strings only: RN call sites keep their own `Easing.*`
// curves (see useAnimations.ts, DockedPanel.tsx) rather than translating a
// bezier string into an `Easing.bezier()` call, so `easings` is web-only.
//
// These are NEW tokens for new motion work. Existing timings
// (DOCK_PANEL_TRANSITION_MS, the 220/180ms in useAnimations.ts) are
// deliberately left alone — visual-audit baselines depend on them, and
// "small, low-risk" means introducing a shared vocabulary, not retuning
// motion that already shipped.
export const durations = {
  instant: 90,
  quick: 150,
  base: 200,
  slow: 280,
} as const;

// Spring for RN `Animated.spring` (arrivals that settle with a little life).
// Spread into the config: Animated.spring(v, { toValue, ...springs.gentle,
// useNativeDriver }). Damping ratio ~0.85: one faint overshoot, no wobble.
export const springs = {
  gentle: { damping: 18, stiffness: 180, mass: 1 },
} as const;

export const easings = {
  standard: 'cubic-bezier(0.2, 0, 0, 1)',
  exit: 'cubic-bezier(0.4, 0, 1, 1)',
} as const;

// Injected once in app/+html.tsx alongside the other raw-CSS <style> tags
// (responsiveBackground, focusVisibleRings). Defines the same tokens above as
// CSS custom properties so web-only motion (map marker CSS, dialog entrance,
// the cold-start splash) reads one shared vocabulary instead of each file
// hardcoding its own numbers, and collapses to near-zero under
// prefers-reduced-motion.
export const MOTION_CSS_VARS = `
:root {
  --redi-dur-instant: ${durations.instant}ms;
  --redi-dur-quick: ${durations.quick}ms;
  --redi-dur-base: ${durations.base}ms;
  --redi-dur-slow: ${durations.slow}ms;
  --redi-ease-standard: ${easings.standard};
  --redi-ease-exit: ${easings.exit};
}
@media (prefers-reduced-motion: reduce) {
  :root {
    --redi-dur-instant: 0ms;
    --redi-dur-quick: 0ms;
    --redi-dur-base: 0ms;
    --redi-dur-slow: 0ms;
  }
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
/* Desktop dialog entrance (AdaptiveSheet.tsx's DialogPresentation). It
 * mounts only while open (no exit animation needed — the caller unmounts
 * it), so this plays once on insertion. Keyed on the dataSet attribute
 * RN-web emits, not a class, because AdaptiveSheet is a shared component and
 * this is the one presentation mode (desktop dialog) that gets it. */
@keyframes redi-dialog-scrim-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes redi-dialog-card-in {
  from { opacity: 0; transform: scale(0.98); }
  to { opacity: 1; transform: scale(1); }
}
[data-redi-anim="dialog-scrim"] {
  animation: redi-dialog-scrim-in var(--redi-dur-base) var(--redi-ease-standard);
}
[data-redi-anim="dialog-card"] {
  animation: redi-dialog-card-in var(--redi-dur-base) var(--redi-ease-standard);
}
/* Toast (Toast.tsx). Mounts only while a toast is showing (ToastHost
 * unmounts it to dismiss), so like the dialog above this plays once on
 * insertion — fade + an 8px rise. */
@keyframes redi-toast-in {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
[data-redi-anim="toast-in"] {
  animation: redi-toast-in var(--redi-dur-base) var(--redi-ease-standard);
}
/* Press feedback (Button.tsx, ListItem.tsx) — a subtle scale-down while
 * actively pressed. Web only; native keeps TouchableOpacity's own opacity
 * change, unchanged. A transition, not a keyframe: there's no one-shot
 * animation to play, just an eased snap to and from the pressed scale. */
[data-redi-press] {
  transition: transform var(--redi-dur-instant) var(--redi-ease-standard);
}
[data-redi-press]:active {
  transform: scale(0.98);
}
/* Tooltip (Tooltip.web.tsx) — plain fade, quick since it's a hover
 * response rather than a deliberate open. */
@keyframes redi-tooltip-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
[data-redi-anim="tooltip-in"] {
  animation: redi-tooltip-in var(--redi-dur-quick) var(--redi-ease-standard);
}
/* List-item enter (useNewItemIds.ts) — a new row landing above the items
 * already on screen: a live feed post, one the viewer just created. Plays
 * once on insertion, same mechanism as the toast above; never touches
 * width/height, so it can't shift layout beside the map. Only opacity +
 * transform, both cheap to animate and exempt from the reduced-motion
 * layout-shift concern. */
@keyframes redi-list-item-in {
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
}
[data-redi-anim="list-item-in"] {
  animation: redi-list-item-in var(--redi-dur-base) var(--redi-ease-standard);
}
/* Plain fade-in for content that swaps in place (the strategy block in the
 * plan panel). Opacity only, so it never moves anything. */
@keyframes redi-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
[data-redi-anim="fade-in"] {
  animation: redi-fade-in var(--redi-dur-base) var(--redi-ease-standard);
}
/* Check beside a freshly assigned row: holds, then fades over the last
 * ~15% of its 1.2s life (the store removes it at 1.2s). */
@keyframes redi-check-out {
  0%, 85% { opacity: 1; }
  100% { opacity: 0; }
}
[data-redi-anim="check-out"] {
  animation: redi-check-out 1200ms linear forwards;
}
/* Nudge on a delete press the re-arm guard swallowed. */
@keyframes redi-nudge {
  0% { transform: scale(1); }
  40% { transform: scale(1.3); }
  100% { transform: scale(1); }
}
[data-redi-anim="nudge"] {
  animation: redi-nudge var(--redi-dur-base) var(--redi-ease-standard);
}
`;
